import type { AuthState } from '@classroom-ai/ui/auth/types';

import { onAuthStateChanged } from 'firebase/auth';
import { useSetState } from 'minimal-shared/hooks';
import { useMemo, useEffect, useCallback } from 'react';
import { AuthContext } from '@classroom-ai/ui/auth/context/auth-context';
import { UserRole, upsertUser, touchUserLogin } from '@dataconnect/generated';

import axios from '../../../lib/axios';
import { AUTH } from '../../../lib/firebase';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({ user: null, loading: true });

  const checkUserSession = useCallback(async () => {
    try {
      onAuthStateChanged(AUTH, async (user: AuthState['user']) => {
        if (user && user.emailVerified) {
          const { accessToken } = user;

          setState({ user, loading: false });
          axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

          // Touch lastLogin; create the row on the first sign-in only, with the role from
          // the JWT custom claim (defaults to STUDENT for users with no claim). After that
          // the login path never rewrites displayName or role (epic #595).
          const touched = await touchUserLogin({ email: user.email ?? '' });
          if (!touched.data.user_update) {
            const tokenResult = await user.getIdTokenResult();
            const role = (tokenResult.claims.role as UserRole) ?? UserRole.STUDENT;
            await upsertUser({
              email: user.email ?? '',
              displayName: user.displayName ?? undefined,
              role,
            });
          }
        } else {
          setState({ user: null, loading: false });
          delete axios.defaults.headers.common.Authorization;
        }
      });
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            id: state.user?.uid,
            accessToken: state.user?.accessToken,
            displayName: state.user?.displayName,
            photoURL: state.user?.photoURL,
            role: state.user?.role ?? 'admin',
          }
        : null,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [checkUserSession, state.user, status]
  );

  return <AuthContext value={memoizedValue}>{children}</AuthContext>;
}
